import { Box, Button,  Divider,  Grid,  IconButton,  Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getData, postData } from "../../services/base.service";
import AutocompleteComponent from "../Shared/AutocompleteComponent";
import {  Edit, Save, Lock, Person, Stars, Delete, Task, Home, History, People } from "@mui/icons-material";
import { useMsal, useAccount } from '@azure/msal-react';
import {  PartnerDetailsType, PartnersAvailableOptions } from "../../types/dataTypes";
import {  partnerCollabHistoryGridDefinition, partnerContactsGridDefinition, partnerExpertGridDefinition, } from "../../utils/consts";
import EditableDataGrid from "../Shared/EditableDataGrid";
import ImageUpload from "../Shared/ImageUpload";
import { fileToBase64, openExternalLink } from "../../utils/utilFunctions";
import { useNotification } from "../Shared/NotificationProvider";

type ExpertFormProps = {
  partnerData?: PartnerDetailsType,
  type: string,
  setLoading: Function,
  setDetailsDrawerOpen: Function,
  setFilters: Function,
  partnerID?: number
  partnerOptions?: PartnersAvailableOptions
  setReload: Function
}

function PartnersForm({ partnerData, type, setLoading, setFilters, setDetailsDrawerOpen, partnerID, partnerOptions, setReload }: ExpertFormProps) {
  const [formData, setFormData] = useState<PartnerDetailsType>();
  const [tabValue, setTabValue] = useState(0);
  const [isEditMode, setIsEditMode] = useState(type === 'add');
  const [saveFormData] = useState(false);
  const [availableReferences, setAvailableReferences] = useState<any[]>([]);
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const notify = useNotification();

  //load the available projects for the preselection in the collaboration history grid when isEditMode is true
  useEffect(() => {
    if (isEditMode && tabValue === 2) {
      console.log('Loading available references for preselection');
      getData('partners/referencesavailable').then((response) => {
        setAvailableReferences(response.collaborations);
      });
    }
  }, [isEditMode,tabValue]);

  useEffect(() => {
    setFormData(partnerData)
  }, [partnerData]);

  const handleTabChange = (event: any, newValue: any) => {
    setTabValue(newValue);
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    //notify the user that the form is in edit mode
    notify(isEditMode ? 'Form is now in view mode. No changes possible now.' :
      'Form is now in edit mode. You can now edit the form and dont forget to hit save in the end.', 'info');
  };

  // Add specific handler for logo file
  const handleLogoChange = (file: File | null) => {
    setLogoFile(file);
    // Optionally update formData with a temporary URL or file reference
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      handleChange('profile.logo')(null, objectUrl);
    } else {
      handleChange('profile.logo')(null, null);
    }
  };

  // change Input based on the name attribute of the input field:
  const handleChange = (path: string) => (event: any, value?: any) => {
    // Handle both regular input events and autocomplete value changes
    const newValue = value !== undefined ? value : event.target.value;
    
    setFormData((prevData: any) => {
      if (!prevData) return prevData;
      
      const newData = { ...prevData };
      const keys = path.split('.');
      let current = newData;
      
      // Navigate to the nested object, creating copies along the way
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      
      // Set the final value
      current[keys[keys.length - 1]] = newValue;
      
      return newData;
    });
  };

  const handleSave = async (type: string) => {
    if (!formData) {
      notify('No data to save', 'warning');
      return;
    }

    setLoading(true);

    try {
      // Update housekeeping fields
      const updatedFormData = { ...formData };
      
      if (type === 'add') {
        updatedFormData.housekeeping.created_by = account?.username || 'unknown';
        updatedFormData.housekeeping.created_at = new Date();
      } else {
        updatedFormData.housekeeping.updated_at = new Date();
        updatedFormData.housekeeping.updated_by = account?.username || 'unknown';
      }

          // Handle logo file upload
      let logoData = null;
      if (logoFile) {
        logoData = {
          file: await fileToBase64(logoFile),
          fileName: logoFile.name,
          mimeType: logoFile.type,
          size: logoFile.size
        };
      }

      const savePayload = {
        formData: updatedFormData,
        ...(logoData && { logoData })
      };


      let response;
      if (type === 'add') {
        response = await postData('partners/addpartner', {savePayload:savePayload});
        notify('Partner added successfully', 'success');
      } else if (type === 'update' && partnerID) {
        response = await postData(`partners/update/${partnerID}`, {savePayload:savePayload});
        notify('Partner updated successfully', 'success');
      }
      console.log('Save response:', response);
      
      // Close drawer and reset filters
      setDetailsDrawerOpen(false);
      

    } catch (error) {
      console.error('Error saving partner:', error);
      notify('Error saving partner. Please try again.', 'error');
    } finally {
      setLoading(false);
      setReload((prev: any) => !prev);
    }
  };


  // const formvalidityCheck = (expertDetails: any): boolean => {
  //   let requiredFieldList = requiredFieldsExpertsForm;
  //   //check if all required fields are filled via a loop over the required fields and check if they are not undefined or empty
  //   //if they are, return false and log the error
  //   for (const field of requiredFieldList) {
  //     if (expertDetails[field as keyof any] === undefined || expertDetails[field as keyof any] === '') {
  //       console.error(`Validation error: ${field} is required.`);
  //       return false;
  //     }
  //   }
  //   return true;
  // }

  const handleDelete = async () => {
    console.log('Deleting partner with ID:', partnerID);
    if (!partnerID) return;

    const isConfirmed = window.confirm("Are you sure you want to delete this partner?");
    if (!isConfirmed) return;

    setLoading(true);
    try {
      await postData(`partners/delete/${partnerID}`, {});
      notify('Partner deleted successfully', 'success');
      setDetailsDrawerOpen(false);
      setFilters({
        names: [],
        languages: [],
        regions: [],
        donors: [],
        keywords: [],
        fulltext: [],
        nationalities: [],
      });
    } catch (error) {
      console.error('Error deleting partner:', error);
      notify('Error deleting partner. Please try again.', 'error');
    } finally {
      setLoading(false);
      setReload((prev: any) => !prev);
    }
  };

  if (!formData) {
    return <div>Loading...</div>;
  }


  return (
    <div>
      <Grid container spacing={2} sx={{
        borderBottom: 1,
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        background: "white",
        zIndex: 1100,
        boxShadow: 1
      }}>
        <Grid container alignItems="center">
          <Grid item xs={6} md={6} style={{ display: 'flex', alignItems: 'center', padding: 10 }}>
          </Grid>
          <Grid item xs={6} md={6} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: 10 }}>
            {type === 'update' && (
              <Button
                variant="outlined"
                startIcon={isEditMode ? <Lock /> : <Edit />}
                onClick={toggleEditMode}
                color="primary"
                style={{ marginRight: 10 }}
              >
                {isEditMode ? 'Lock' : 'Edit'}
              </Button>
            )}
            {(type === 'update' && isEditMode) || type === 'add' ? (
              <Button
                variant="outlined"
                color="primary"
                startIcon={isEditMode ? <Save /> : <Save />}
                style={{ marginRight: 10 }}
                onClick={() => handleSave(type)}
              >
                {'Save'}
              </Button>
            ) : null}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
            <Tab label={<span style={{ fontWeight: 'bold' }}>Profile</span>} {...a11yProps(0)} />
            <Tab label={<span style={{ fontWeight: 'bold' }}>Contact</span>} {...a11yProps(1)} />
            <Tab label={<span style={{ fontWeight: 'bold' }}>Collaboration History</span>} {...a11yProps(2)} />
            <Tab label={<span style={{ fontWeight: 'bold' }}>Partner Experts</span>} {...a11yProps(3)} />
          </Tabs>
        </Grid>
      </Grid>
      <TabPanel value={tabValue} index={0}>
        <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }} paddingTop={2}>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Stars />
              <Typography variant="h6">Profile</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Homepage" value={formData?.profile.website || ''} onChange={handleChange('profile.website')}
              InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditMode }} name='website' />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Remark" value={formData?.profile.remark || ''} onChange={handleChange('profile.remark')}
              multiline rows={3} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: !isEditMode }} name='remark' fullWidth/>
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteComponent
              options={partnerOptions?.fos !== undefined && partnerOptions?.fos !== null ? partnerOptions.fos : formData?.fos_options?.map((fos: string) => fos) || []}
              value={formData?.profile.fos !== undefined && formData?.profile.fos !== null ?
                (formData.profile.fos[0] === null ?
                  undefined : formData.profile.fos) : undefined} handleChange={handleChange('profile.fos')}
              isMulti={true}
              label="Field of Service"
              disabled={!isEditMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AutocompleteComponent
              options={partnerOptions?.countries !== undefined && partnerOptions?.countries !== null ? partnerOptions.countries : formData?.countries_options?.map((fos: string) => fos) || []}
              value={formData?.profile.countries !== undefined && formData?.profile.countries !== null ?
                (formData.profile.countries[0] === null ?
                  undefined : formData.profile.countries) : undefined} handleChange={handleChange('profile.countries')}
              isMulti={true}
              label="Operating Countries"
              disabled={!isEditMode}
            />
          </Grid>
          <Grid item xs={12} md={6}>
           <ImageUpload
            label="Company Logo"
            value={logoFile || formData?.profile.logo}
            onChange={handleLogoChange}
            disabled={!isEditMode}
            maxSize={5}
            acceptedFormats={['image/jpeg', 'image/jpg', 'image/png', 'image/gif']}
            previewSize={120}
          />          
          </Grid>
          <Grid item xs={12} md={6}>
          <TextField 
            label="Folder Link" 
            value={formData?.profile.folder || ''} 
            onChange={handleChange('profile.folder')}
            InputLabelProps={{ shrink: true }} 
            InputProps={{ 
              readOnly: !isEditMode,
              endAdornment: formData?.profile.folder ? (
                <IconButton
                  edge="end"
                  onClick={() => openExternalLink(formData.profile.folder || '')}
                  disabled={!formData.profile.folder}
                  size="small"
                >
                  <Task />
                </IconButton>
              ) : null
            }} 
            name='folder' 
            fullWidth
            placeholder="Enter folder URL"
            helperText={formData?.profile.folder ? "Click the icon to open in new tab" : "Enter a valid URL"}
          />
        </Grid>
          {type === 'update' && (
            <>
              <Grid item xs={12} md={12} paddingTop={2}>
                <Divider style={{ color: 'lightgrey' }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Partner Created By"
                  value={formData?.housekeeping.created_by || ''}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: !isEditMode }}
                  name='created_by'
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Partner Created at"
                  value={formData?.housekeeping.created_at || ''}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: !isEditMode }}
                  name='created_at'
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Partner last Update by"
                  value={formData?.housekeeping.updated_by || ''}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: !isEditMode }}
                  name='updated_by'
                  disabled
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Partner last Update"
                  value={formData?.housekeeping.updated_at || ''}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ readOnly: !isEditMode }}
                  name='updated_at'
                  disabled
                />
              </Grid>
            </>
          )}
          {type === 'update' && isEditMode && (
            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={handleDelete}
              >
                Delete
              </Button>
            </Grid>
          )}
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }} paddingTop={2}>
          <Grid item md={12}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Home />
              <Typography variant="h6">Contact</Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField InputLabelProps={{ shrink: true }} label="Name" name="name" value={formData?.contact.name || ''}
              onChange={handleChange('contact.name')} required InputProps={{ readOnly: !isEditMode }} error={saveFormData && formData?.contact.name === ''} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField InputLabelProps={{ shrink: true }} style={{ width: '100%' }} label="Short Name" name="short_name"
              value={formData?.contact.short_name || ''} onChange={handleChange('contact.short_name')} InputProps={{ readOnly: !isEditMode }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField InputLabelProps={{ shrink: true }} style={{ width: '100%' }} label="Headquarter" name="headquarter"
              value={formData?.contact.headquarter || ''} onChange={handleChange('contact.headquarter')} InputProps={{ readOnly: !isEditMode }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField InputLabelProps={{ shrink: true }} style={{ width: '100%' }} label="Mail" name="email"
              value={formData?.contact.email || ''} onChange={handleChange('contact.email')} InputProps={{ readOnly: !isEditMode }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField InputLabelProps={{ shrink: true }} style={{ width: '100%' }} label="Address" name="address"
              value={formData?.contact.address || ''} onChange={handleChange('contact.address')} InputProps={{ readOnly: !isEditMode }} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField InputLabelProps={{ shrink: true }} style={{ width: '100%' }} label="Phone" name="phone"
              value={formData?.contact.phone || ''} onChange={handleChange('contact.phone')} InputProps={{ readOnly: !isEditMode }} />
          </Grid>
        </Grid>
        <Grid item xs={12} md={12} padding={2}>
          <Divider style={{ color: 'lightgrey' }} />
        </Grid>
        <Grid item md={12}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Person />
            <Typography variant="h6">Contact Persons</Typography>
          </Stack>
        </Grid>
        <Grid container spacing={3} margin={2}>
          <EditableDataGrid 
          col={partnerContactsGridDefinition} 
          rows={formData?.contact.contact_persons || []}  
          editIsOn={isEditMode} 
          formData={formData}
          setFormData={setFormData}
          dataPath="contact.contact_persons"
        />
        </Grid>
      </TabPanel>
      <TabPanel value={tabValue} index={2}>
        <Grid item md={12}>
          <Stack direction="row" alignItems="center" gap={1}>
            <History />
            <Typography variant="h6">Related References</Typography>
          </Stack>
        </Grid>
        <Grid container spacing={3} margin={2}>
      
          <EditableDataGrid 
            col={partnerCollabHistoryGridDefinition} 
            rows={formData?.collab_history || []} 
            editIsOn={isEditMode}
            formData={formData}
            setFormData={setFormData}
            dataPath="collab_history"
            preselectionIsOn={true}
            preselectionList={availableReferences}
            preselectionDisplayField="name"
            preselectionValueField="id"
            readOnlyFields={['project_number', 'project_title', 'completion_date', 'lead']}
        />
        </Grid>
      </TabPanel>
            <TabPanel value={tabValue} index={3}>
        <Grid item md={12}>
          <Stack direction="row" alignItems="center" gap={1}>
            <People />
            <Typography variant="h6">Related Experts</Typography>
          </Stack>
        </Grid>
        {/* <Grid container spacing={3} margin={2}>
          <EditableDataGrid 
            col={partnerExpertGridDefinition} 
            rows={formData?.experts || []} 
            editIsOn={isEditMode}
            formData={formData}
            setFormData={setFormData}
            dataPath="experts"
          />
        </Grid> */}
      </TabPanel>
    </div>
  );
}


function TabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: any) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export default PartnersForm;